/**
 * Client-side event display fields — mirrors MapToBe-Backend Events-v2-bridge inference
 * so M2B-Tools public UI matches MapToDo mobile without extra Firestore fields.
 */
import {
  getStoredEventCategories,
  inferCategoriesFromText,
} from './eventCategories';
import { getPrimaryEditorialPickLabel } from './eventEditorialPick';

/** Hard cap for event titles in every editor. */
export const EVENT_TITLE_MAX = 80;

/**
 * @param {string|undefined|null} description
 * @returns {string|null}
 */
export function extractTimeFromDescription(description) {
  const timeMatch = description?.match(/\b(\d{1,2}):(\d{2})\b/);
  return timeMatch ? timeMatch[0] : null;
}

/**
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string[]}
 */
export function inferEventCategories(event) {
  const stored = getStoredEventCategories(event);
  return stored === null ? inferCategoriesFromText(event) : stored;
}

/**
 * Public category pills + filter labels: highest editorial pick (if any), then content categories.
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string[]}
 */
export function getEventPublicCategories(event) {
  const pickLabel = getPrimaryEditorialPickLabel(event?.editorialPick, event);
  const categories = inferEventCategories(event);
  if (!pickLabel) return categories;
  return [pickLabel, ...categories.filter((category) => category !== pickLabel)];
}

/**
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string|null}
 */
export function extractEventStartTime(event) {
  if (typeof event?.startTime === 'string' && event.startTime.trim()) {
    return event.startTime.trim();
  }
  return extractTimeFromDescription(event?.description);
}

/**
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string|null}
 */
export function extractEventEndTime(event) {
  if (typeof event?.endTime === 'string' && event.endTime.trim()) {
    return event.endTime.trim();
  }
  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string|null}
 */
export function extractEventDoorTime(event) {
  if (typeof event?.doorTime === 'string' && event.doorTime.trim()) {
    return event.doorTime.trim();
  }
  return null;
}

/**
 * Time span for cards: `20:00`, `20:00–02:00`, or with doors `Doors 19:00 · 20:00`.
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string|null}
 */
export function formatEventTimeSpan(event) {
  const startTime = extractEventStartTime(event);
  const endTime = extractEventEndTime(event);
  const doorTime = extractEventDoorTime(event);

  let span = null;
  if (startTime && endTime) span = `${startTime}–${endTime}`;
  else if (startTime) span = startTime;
  else if (endTime) span = `until ${endTime}`;

  if (doorTime && span) return `Doors ${doorTime} · ${span}`;
  if (doorTime) return `Doors ${doorTime}`;
  return span;
}

/**
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string}
 */
export function formatEventDateLine(event) {
  const dateStr = event?.dateStr;
  if (!dateStr) return '';

  const timeSpan = formatEventTimeSpan(event);
  return timeSpan ? `${dateStr} · ${timeSpan}` : dateStr;
}

/**
 * Public price label (`Free`, `15 KM`, …) or null when unset.
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string|null}
 */
export function formatEventPrice(event) {
  const raw = typeof event?.price === 'string' ? event.price.trim() : '';
  return raw || null;
}

/**
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string|null}
 */
export function formatEventOrganizer(event) {
  const raw = typeof event?.organizer === 'string' ? event.organizer.trim() : '';
  return raw || null;
}

/**
 * @param {Record<string, unknown>|null|undefined} event
 * @returns {string|null}
 */
export function formatEventAgeNote(event) {
  const raw = typeof event?.ageNote === 'string' ? event.ageNote.trim() : '';
  return raw || null;
}

